/*
 * Beneos Asset Freshness Guard
 *
 * Faengt kaputte Thumbnails ab, die auftreten, wenn ein Tab nach langem
 * Standby oder Rechner-Neustart in einer veralteten Session wiederhergestellt
 * wird. Zwei Ursachen werden abgedeckt:
 *   1. Netz noch nicht verbunden beim Kaltstart -> Bild-Load scheitert einmalig.
 *      Heilt ein simpler Retry (gleiche URL neu laden).
 *   2. Abgelaufene signierte URL (exp in der Vergangenheit) -> Bild bleibt
 *      dauerhaft kaputt. Heilt nur ein Reload der Seite (frische URL-Mint).
 *
 * Strategie: kaputte Bilder still 1-2x neu laden. Bleiben mehrere Bilder
 * systemisch kaputt (echte Ablauf-Situation), einmalig ein Overlay
 * "Sitzung abgelaufen, bitte neu laden" zeigen. Zusaetzlich beim erneuten
 * Sichtbarwerden des Tabs (visibilitychange / bfcache pageshow) proaktiv die
 * bereits kaputten Bilder neu laden, bevor der Nutzer sie ueberhaupt sieht.
 *
 * Selbststaendig, keine Abhaengigkeiten. Strings sind absichtlich inline
 * (EN + DE), damit das Overlay auch dann funktioniert, wenn wegen fehlender
 * Netzverbindung externe Sprachdateien nicht geladen werden konnten.
 */
(function () {
  "use strict";
  if (window.__beneosFreshnessGuard) return;
  window.__beneosFreshnessGuard = true;

  var cfg = window.__BENEOS_FRESHNESS__ || {};
  var MAX_RETRY = cfg.maxRetry || 2;
  var BACKOFF = cfg.backoff || [400, 1200];
  var THRESHOLD = cfg.threshold || 4;
  var FRESHNESS_MS = cfg.freshnessMs || 4 * 60 * 1000;
  var FAIL_WINDOW_MS = cfg.failWindowMs || 8000;

  var STR = buildStrings(cfg.strings);
  var lastActive = Date.now();
  var recentFails = [];
  var expiredFails = [];
  var overlayShown = false;
  var sessionCheckAt = 0;
  var sessionCheckInflight = false;

  // Nur echte Inhalts-Bilder ueberwachen (keine data:-Pixel, keine leeren src).
  function isThumb(img) {
    if (!img || img.tagName !== "IMG") return false;
    var s = img.currentSrc || img.getAttribute("src") || "";
    if (!s || s.indexOf("data:") === 0) return false;
    return true;
  }

  // Signierte URLs tragen ihr Ablaufdatum selbst (exp=<unix>). Abgelaufen =
  // deterministisch "Reload noetig": kein Retry kann das heilen, und der
  // Session-Check darf das Overlay dann NICHT unterdruecken (der Nutzer ist
  // ja weiterhin eingeloggt, nur die URL-Signatur ist tot). 30s Toleranz
  // gegen Uhren-Schieflage.
  function urlExpired(s) {
    var m = /[?&]exp=(\d+)/.exec(s || "");
    if (!m) return false;
    return (parseInt(m[1], 10) + 30) * 1000 < Date.now();
  }
  function expiredSig(img) {
    return urlExpired((img && (img.currentSrc || img.getAttribute("src"))) || "");
  }

  // Endgueltig kaputte Thumbnails nicht als Browser-Broken-Icon stehen lassen,
  // sondern durch eine gestaltete Kachel ersetzen: dunkles Grau im Theme-Ton
  // mit diffuser BENEOS-Wortmarke. Eingebettetes SVG (data-URI, kein Netz-
  // Request, skaliert scharf); die Original-URL bleibt in dataset.bfgBase,
  // damit reloadBroken transiente Faelle spaeter noch heilen kann.
  var PLACEHOLDER = "data:image/svg+xml," + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 225">'
    + '<defs><radialGradient id="g" cx="50%" cy="45%" r="75%">'
    + '<stop offset="0%" stop-color="#221d16"/><stop offset="100%" stop-color="#15120e"/>'
    + '</radialGradient></defs>'
    + '<rect width="400" height="225" fill="url(#g)"/>'
    + '<text x="200" y="118" text-anchor="middle" font-family="Georgia,\'Times New Roman\',serif" '
    + 'font-size="26" letter-spacing="10" fill="#c9a961" opacity="0.25">BENEOS</text>'
    + '</svg>'
  );

  function applyPlaceholder(img) {
    if (!img || img.dataset.bfgPlaceholder === "1") return;
    if (!img.dataset.bfgBase) {
      img.dataset.bfgBase = img.currentSrc || img.getAttribute("src") || "";
    }
    img.dataset.bfgPlaceholder = "1";
    img.src = PLACEHOLDER;
  }

  // Additiver Cachebuster. Signatur-Schemata hier verifizieren ueber feste
  // Felder und ignorieren unbekannte Query-Params, daher unkritisch.
  function withBust(url, tag) {
    if (!url) return url;
    url = url.replace(/([?&])_bfg=[^&]*(&|$)/, function (m, p1, p2) { return p2 === "&" ? p1 : ""; });
    url = url.replace(/[?&]$/, "");
    var sep = url.indexOf("?") >= 0 ? "&" : "?";
    return url + sep + "_bfg=" + tag;
  }

  function scheduleRetry(img) {
    if (expiredSig(img)) { applyPlaceholder(img); registerPersistentFail(true); return; }
    var n = parseInt(img.dataset.bfgRetry || "0", 10);
    if (n >= MAX_RETRY) { applyPlaceholder(img); registerPersistentFail(false); return; }
    if (!img.dataset.bfgBase) {
      img.dataset.bfgBase = img.currentSrc || img.getAttribute("src") || "";
    }
    if (!img.dataset.bfgBase) return;
    n += 1;
    img.dataset.bfgRetry = String(n);
    var delay = BACKOFF[Math.min(n - 1, BACKOFF.length - 1)];
    setTimeout(function () {
      if (img.complete && img.naturalWidth > 0) { img.dataset.bfgRetry = "0"; return; }
      img.src = withBust(img.dataset.bfgBase, n);
    }, delay);
  }

  function registerPersistentFail(isExpired) {
    var now = Date.now();
    recentFails.push(now);
    recentFails = recentFails.filter(function (t) { return now - t < FAIL_WINDOW_MS; });
    if (isExpired) {
      expiredFails.push(now);
      expiredFails = expiredFails.filter(function (t) { return now - t < FAIL_WINDOW_MS; });
      // Zwei abgelaufene Signaturen im Fenster sind Beweis genug: Overlay
      // direkt, ohne Session-Check (die Session lebt in diesem Fall meist noch).
      if (expiredFails.length >= 2) { maybeShowOverlay(true); return; }
    }
    if (recentFails.length >= THRESHOLD) maybeShowOverlay(false);
  }

  // Ein 404 auf eine schlicht fehlende Datei ist KEIN Session-Problem und ein
  // Reload heilt es nie. Wenn die Seite eine sessionCheckUrl konfiguriert,
  // wird die Session erst verifiziert: antwortet der Endpoint gesund
  // (HTTP ok UND result nicht "error"), fehlen nur einzelne Dateien -> kein
  // Overlay, Fehlerfenster leeren. Nur wenn der Check scheitert (401 /
  // not_logged_in / Netzfehler), erscheint das Overlay. Ohne konfigurierten
  // Check bleibt das bisherige Verhalten (Overlay ab Schwelle).
  // Ausnahme expired=true: abgelaufene URL-Signaturen sind per exp-Param
  // bewiesen; ein Reload ist IMMER die richtige Aktion (Session lebt ->
  // frische URLs, Session tot -> Login-Prompt), also Overlay ohne Check.
  function maybeShowOverlay(expired) {
    if (overlayShown) return;
    if (expired) { showOverlay(); return; }
    var url = cfg.sessionCheckUrl || "";
    if (!url || typeof window.fetch !== "function") { showOverlay(); return; }
    var now = Date.now();
    if (sessionCheckInflight || now - sessionCheckAt < 60000) return;
    sessionCheckInflight = true;
    sessionCheckAt = now;
    fetch(url, { credentials: "same-origin", headers: { "Accept": "application/json" } })
      .then(function (r) {
        if (!r.ok) throw new Error("http " + r.status);
        return r.json();
      })
      .then(function (j) {
        var result = j && typeof j.result === "string" ? j.result.toLowerCase() : "";
        if (result === "error") { showOverlay(); return; }
        recentFails = [];   // session healthy: files are just missing, no prompt
      })
      .catch(function () { showOverlay(); })
      .then(function () { sessionCheckInflight = false; });
  }

  // Beim erneuten Sichtbarwerden die bereits kaputten Bilder neu laden.
  // Platzhalter-Kacheln zaehlen mit: transiente Faelle bekommen einen neuen
  // Versuch mit der Original-URL; abgelaufene Signaturen bleiben Platzhalter
  // (ein Retry kann sie nie heilen, das Overlay fordert den Reload an).
  function reloadBroken() {
    var imgs = document.querySelectorAll("img");
    Array.prototype.forEach.call(imgs, function (img) {
      var isPlaceholder = img.dataset && img.dataset.bfgPlaceholder === "1";
      if (!isPlaceholder && !isThumb(img)) return;
      if (!isPlaceholder && img.complete && img.naturalWidth > 0) return;
      var base = img.dataset.bfgBase || img.currentSrc || img.getAttribute("src");
      if (!base || base.indexOf("data:") === 0) return;
      if (urlExpired(base)) return; // bleibt Platzhalter, Reload-Overlay uebernimmt
      img.dataset.bfgBase = base;
      img.dataset.bfgRetry = "0";
      delete img.dataset.bfgPlaceholder;
      img.src = withBust(base, "v" + Math.floor(Date.now() / 1000));
    });
  }

  function onVisible(persisted) {
    var hiddenFor = Date.now() - lastActive;
    lastActive = Date.now();
    if (persisted || hiddenFor > FRESHNESS_MS) reloadBroken();
  }

  document.addEventListener("error", function (e) {
    if (isThumb(e.target)) scheduleRetry(e.target);
  }, true);

  document.addEventListener("load", function (e) {
    var img = e.target;
    if (!img || img.tagName !== "IMG" || !img.dataset) return;
    if (img.dataset.bfgRetry) img.dataset.bfgRetry = "0";
    // Echtes Bild geladen (kein data:-Platzhalter): Platzhalter-Marker aufheben.
    var src = img.currentSrc || img.getAttribute("src") || "";
    if (img.dataset.bfgPlaceholder === "1" && src.indexOf("data:") !== 0) {
      delete img.dataset.bfgPlaceholder;
    }
  }, true);

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") onVisible(false);
    else lastActive = Date.now();
  });
  window.addEventListener("pageshow", function (e) { if (e && e.persisted) onVisible(true); });

  function showOverlay() {
    if (overlayShown) return;
    overlayShown = true;
    var back = document.createElement("div");
    back.setAttribute("role", "dialog");
    back.setAttribute("aria-modal", "true");
    back.style.cssText = "position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(8,10,14,.72);backdrop-filter:blur(3px);-webkit-backdrop-filter:blur(3px);font-family:'Inter',system-ui,-apple-system,sans-serif";

    var card = document.createElement("div");
    card.style.cssText = "max-width:420px;width:100%;background:var(--bg-card,#15181c);border:1px solid var(--gold,#c9a24b);border-radius:14px;padding:26px 24px;text-align:center;color:var(--ink,#e8e6e0);box-shadow:0 24px 60px rgba(0,0,0,.6)";

    var h = document.createElement("div");
    h.style.cssText = "font-family:'Marcellus',serif;font-size:20px;line-height:1.2;margin-bottom:10px;color:var(--gold,#c9a24b)";
    h.textContent = STR.title;

    var p = document.createElement("div");
    p.style.cssText = "font-size:14px;line-height:1.5;opacity:.88;margin-bottom:20px";
    p.textContent = STR.body;

    var btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = STR.reload;
    btn.style.cssText = "appearance:none;cursor:pointer;border:0;border-radius:30px;padding:11px 26px;font-size:14px;font-weight:600;background:var(--gold,#c9a24b);color:#1a1305";
    btn.addEventListener("click", function () { window.location.reload(); });

    var dismiss = document.createElement("button");
    dismiss.type = "button";
    dismiss.textContent = STR.dismiss;
    dismiss.style.cssText = "display:block;margin:14px auto 0;background:none;border:0;color:var(--ink,#e8e6e0);opacity:.6;font-size:12.5px;cursor:pointer;text-decoration:underline";
    dismiss.addEventListener("click", function () { if (back.parentNode) back.parentNode.removeChild(back); });

    card.appendChild(h);
    card.appendChild(p);
    card.appendChild(btn);
    card.appendChild(dismiss);
    back.appendChild(card);
    (document.body || document.documentElement).appendChild(back);
  }

  // Faellt die Bruecke aus, gilt der englische Text. Nie ein Fehler.
  function bt(k, d) { return (window.Beneos && window.Beneos.t) ? window.Beneos.t(k, d) : d; }

  function buildStrings(override) {
    // Frueher stand hier eine fest verdrahtete Zweisprachigkeit: Deutsch, wenn
    // lang mit "de" beginnt, sonst Englisch. Die elf uebrigen Sprachen bekamen
    // Englisch. Jetzt liefert die Bruecke jede Sprache, der deutsche Text von
    // damals steht als Uebersetzung in locales/de.json.
    var base = {
      title: bt("common.guard_title", "Session expired"),
      body: bt("common.guard_body", "Some preview images could not load because this tab has been open too long. Reload the page to bring everything back."),
      reload: bt("common.guard_reload", "Reload page"),
      dismiss: bt("common.guard_dismiss", "Later")
    };
    if (override) {
      for (var k in override) { if (Object.prototype.hasOwnProperty.call(override, k)) base[k] = override[k]; }
    }
    return base;
  }
})();
